import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import { Modal } from 'react-bootstrap';
import { Typography, NewIconButton, NewDropdown } from 'app/components';
import { useImportedTaxonomies, DocumentTaxonomyConfigTaxonomy } from 'entities/tag';

import './Modal.scss';
import { formatTaxonomyName } from 'app/utils/tag-utils';

interface AddTaxonomyModalProps {
  show: boolean;
  connectedTaxonomies: DocumentTaxonomyConfigTaxonomy[];
  onClose: () => void;
  onAdd: (taxonomyId: string) => void;
}

export const AddTaxonomyModal = ({ show, connectedTaxonomies, onClose, onAdd }: AddTaxonomyModalProps) => {
  const { t } = useTranslation(['common', 'tag']);

  const { data: importedTaxonomies = [] } = useImportedTaxonomies();
  const [selectedTaxonomyId, setSelectedTaxonomyId] = useState<string>('');

  const connectedTaxonomyIds = connectedTaxonomies.map((tax) => tax.taxonomyId);
  const availableTaxonomies = importedTaxonomies.filter((tax) => !connectedTaxonomyIds.includes(tax.id));
  const hasAvailable = availableTaxonomies.length > 0;

  const handleAdd = () => {
    if (selectedTaxonomyId) {
      onAdd(selectedTaxonomyId);
      setSelectedTaxonomyId('');
      onClose();
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (selectedTaxonomyId && hasAvailable) handleAdd();
  };

  const handleClose = () => {
    setSelectedTaxonomyId('');
    onClose();
  };

  return (
    <Modal show={show} onHide={handleClose} contentClassName="taxonomy-modal">
      <Modal.Body className="taxonomy-modal__content">
        <form onSubmit={handleSubmit}>
          <div className="taxonomy-modal__header">
            <Typography variant="body-8" fontWeight="semibold">
              {t('tag:load.taxonomies')}
            </Typography>
            <NewIconButton icon="close" variant="transparent" type="button" onClick={handleClose} />
          </div>

          <div className="form-group">
            <Typography variant="body-7" fontWeight="semibold">
              {t('tag:project.tax.label')}
            </Typography>

            {hasAvailable ? (
              <NewDropdown
                fullwidth
                label={!selectedTaxonomyId ? t('tag:taxonomy.select.placeholder') : undefined}
                options={availableTaxonomies.map((tax) => ({
                  value: tax.id,
                  label: formatTaxonomyName(tax.name),
                }))}
                currentValue={selectedTaxonomyId}
                onUpdate={(newValue) => setSelectedTaxonomyId(newValue)}
              />
            ) : (
              <NewDropdown fullwidth label={t('tag:taxonomy.none.available')} options={[]} currentValue="" onUpdate={() => {}} />
            )}
          </div>

          <div className="taxonomy-modal__footer">
            <button type="submit" className="btn btn-primary" disabled={!selectedTaxonomyId || !hasAvailable}>
              {t('common:add')}
            </button>
            <button type="button" className="btn btn-secondary" onClick={handleClose}>
              {t('common:cancel')}
            </button>
          </div>
        </form>
      </Modal.Body>
    </Modal>
  );
};
